import clsx from 'clsx';
import React from 'react';
import FormField from '../../molecules/FormField/FormField';
import Icon from '../Icon/Icon';
import useInputSearch from './useInputSearch';

const DEFAULT_MAX_RECENT_SEARCHES = 6;

export type InputSearchLabels = {
  clearItemButtonAriaLabel?: string;
  searchLabel?: string;
};

export type InputSearchTexts = {
  dropdownTitle?: string;
  emptyItemsText?: string;
  filtersButtonText?: string;
};

type ItemsDropdownProps = Readonly<{
  emptyItemsText: string;
  items: string[];
  maxItems: number;
  onClickItem: (item: string) => void;
  onRemoveItem: (item: string) => void;
  clearItemButtonAriaLabel: string;
  title: string;
}>;

function ItemsDropdown({ emptyItemsText, items, maxItems, onClickItem, onRemoveItem, clearItemButtonAriaLabel, title }: ItemsDropdownProps): JSX.Element {
  function handleOnClickItem(item: string) {
    return function () {
      onClickItem(item);
    };
  }

  function handleRemoveItem(item: string) {
    return function () {
      onRemoveItem(item);
    };
  }

  return (
    <div className="input-search__dropdown">
      <p className="input-search__dropdown-title">{title}</p>
      {items.length > 0 ? (
        <ul className="input-search__dropdown-list">
          {items.slice(0, maxItems).map((item) => (
            <li className="input-search__dropdown-item" key={item}>
              <button className="input-search__dropdown-item-button" onClick={handleOnClickItem(item)} type="button">
                <Icon name="search" size="md" />
                <span className="input-search__dropdown-item-text">{item}</span>
              </button>
              <button
                aria-label={clearItemButtonAriaLabel}
                className="input-search__dropdown-item-remove-button"
                onClick={handleRemoveItem(item)}
                type="button"
              >
                <Icon name="close" size="lg" />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="input-search__dropdown-empty">{emptyItemsText}</p>
      )}
    </div>
  );
}

// TODO: Discuss with team the filters redesing. See mdx Follow up section for details.
// Kept isolated in its own function so it's easy to remove from here later.
type FiltersButtonProps = Readonly<{
  filtersButtonText: string;
  onClick: () => void;
}>;

function FiltersButton({ filtersButtonText, onClick }: FiltersButtonProps): JSX.Element {
  return (
    <button className="input-search__filters" onClick={onClick} type="button">
      <Icon name="filters" size="md" />
      <span className="input-search__filters-text">{filtersButtonText}</span>
    </button>
  );
}

type InputSearchProps = Readonly<{
  className?: string;
  debounceMs?: number;
  disabled?: boolean;
  iconPosition?: 'left' | 'right';
  labels?: InputSearchLabels;
  maxRecentSearches?: number;
  onChange?: (value: string) => void;
  onClickFilters?: () => void;
  onClickRemoveSearch?: (item: string) => void;
  placeholder?: string;
  recentSearches?: string[];
  showFilters?: boolean;
  texts?: InputSearchTexts;
  value?: string;
}>;

const InputSearch = React.forwardRef<HTMLInputElement, InputSearchProps>(
  (
    {
      className,
      debounceMs = 500,
      disabled = false,
      iconPosition = 'left',
      labels,
      maxRecentSearches = DEFAULT_MAX_RECENT_SEARCHES,
      onChange,
      onClickFilters = () => {},
      onClickRemoveSearch = () => {},
      placeholder,
      recentSearches = [],
      showFilters = false,
      texts,
      value,
    },
    ref
  ) => {
    const {
      containerRef,
      handleChange,
      handleKeyDown,
      inputVal,
      onRepeatSearch,
      onShowRecentSearches,
      showRecentSearches,
      t,
    } = useInputSearch({ debounceMs, onChange, recentSearches, value });

    return (
      <div
        className={clsx('input-search', `input-search--icon-${iconPosition}`, className, { 'input-search--disabled': disabled })}
        ref={containerRef}
      >
        <FormField isDisabled={disabled} isLabelHidden label={labels?.searchLabel ?? t('actions:Search')}>
          <input
            className="input-search__input"
            onChange={handleChange}
            onClick={onShowRecentSearches}
            onFocus={onShowRecentSearches}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            ref={ref}
            type="search"
            value={inputVal}
          />
        </FormField>

        <span aria-hidden="true" className="input-search__icon">
          <Icon name="search" size="lg" />
        </span>

        {showFilters && (
          <FiltersButton filtersButtonText={texts?.filtersButtonText ?? t('common:Filter')} onClick={onClickFilters} />
        )}

        {showRecentSearches && (
          <ItemsDropdown
            emptyItemsText={texts?.emptyItemsText ?? t('common:No recent searches')}
            items={recentSearches}
            maxItems={maxRecentSearches}
            onClickItem={onRepeatSearch}
            onRemoveItem={onClickRemoveSearch}
            clearItemButtonAriaLabel={labels?.clearItemButtonAriaLabel ?? t('common:Delete')}
            title={texts?.dropdownTitle ?? t('common:Recent searches')}
          />
        )}
      </div>
    );
  }
);

InputSearch.displayName = 'InputSearch';

export default InputSearch;
